import { api, unwrap } from "./api";
import { STOPLIGHT } from "./stoplight";

export type NotificationType =
  | "entered_review"
  | "accepted"
  | "changes_requested"
  | "comment";

export type NotificationItem = {
  id: string;
  type: NotificationType;
  taskId: string | null;
  title: string | null;
  actorName: string | null;
  readAt: string | null;
  createdAt: string;
};

export type NotificationPage = {
  notifications: NotificationItem[];
  hasMore: boolean;
  unread: number;
};

export async function fetchNotifications(
  limit: number,
  offset: number,
): Promise<NotificationPage> {
  return unwrap(
    api.api.notifications.get({
      query: { limit: String(limit), offset: String(offset) },
    }),
  );
}

export async function fetchUnreadCount(): Promise<number> {
  const { unread } = await unwrap(api.api.notifications["unread-count"].get());
  return unread;
}

export async function markAllNotificationsRead(): Promise<void> {
  await unwrap(api.api.notifications["read-all"].post());
}

export async function markNotificationRead(id: string): Promise<void> {
  await unwrap(api.api.notifications({ id }).read.post());
}

/** Human message for a notification. */
export function describeNotification(n: NotificationItem): string {
  const who = n.actorName ?? "Someone";
  const title = n.title ?? "a task";
  switch (n.type) {
    case "entered_review":
      return `New task to review: ${title}`;
    case "accepted":
      return `${who} accepted “${title}”`;
    case "changes_requested":
      return `${who} requested changes on “${title}”`;
    case "comment":
      return `${who} commented on “${title}”`;
    default:
      return title;
  }
}

/** Accent color per notification type (stoplight palette). */
export const NOTIFICATION_COLOR: Record<NotificationType, string> = {
  entered_review: STOPLIGHT.amber,
  accepted: STOPLIGHT.green,
  changes_requested: STOPLIGHT.red,
  comment: "#8B8B8B",
};

export type NotificationsPreviewView = "loading" | "error" | "empty" | "list";

/**
 * Which state the header bell's preview dropdown should render. A failed fetch
 * gets its OWN state — otherwise an empty list after an error reads as "you're
 * all caught up", silently masking the failure.
 */
export function notificationsPreviewView(opts: {
  open: boolean;
  isPending: boolean;
  isError: boolean;
  count: number;
}): NotificationsPreviewView {
  if (opts.isPending && opts.open) return "loading";
  if (opts.isError) return "error";
  return opts.count === 0 ? "empty" : "list";
}

/** Compact relative time: "just now", "5m", "3h", "2d", then a date. */
export function timeAgo(iso: string): string {
  const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}
